import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Alert,
  Divider,
  TextField,
  Autocomplete,
  Tooltip,
  styled,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Language as LanguageIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Upload as UploadIcon,
  Add as AddIcon,
} from '@mui/icons-material';

// SAP Design System
import { SAP_COLORS, SAP_TYPOGRAPHY, SAP_SPACING, SAP_SHADOWS } from '../theme/sapDesignSystem';
import { SAPIconButton, PageHeader } from '../components';

import { fetchTranslationMappers, fetchTranslationMappersCount, fetchTranslationData, fetchTranslationDataCount, fetchAllTranslationMapperCodes, fetchClientTranslationWithSearch, fetchClientTranslationCountWithSearch, deleteTranslationData, createTranslationData, updateTranslationData } from '../api/features/clientTranslation/clientTranslationApi';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { TranslationMapper, TranslationData } from '../api/features/clientTranslation/clientTranslationtypes';
import { useClientDropdown } from '../hooks/common';
import { fetchClients } from '../api/features/clientData/clientApi';
import type { SAPInputOption } from '../components/design-system';

// SAP Fiori Client Translation Components - matching other pages exactly

const SAPFilterCard = styled(Card)(({ theme }) => ({
  marginBottom: SAP_SPACING.lg,
  borderRadius: '8px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.card,
}));

const SAPDataCard = styled(Card)(({ theme }) => ({
  borderRadius: '8px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.card,
}));

const SAPTableHeader = styled(TableHead)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapLightBlue,
  '& .MuiTableCell-head': {
    fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
    color: SAP_COLORS.sapGray6,
    borderBottom: `2px solid ${SAP_COLORS.sapBlue}`,
  },
}));

export default function ClientTranslation() {
  const { clientCode } = useClientCode();
  const isAdmin = clientCode === 'admin';
  const { data: clientOptions = [] } = useClientDropdown();
  
  // State for first page (code list)
  const [codeOptions, setCodeOptions] = useState<TranslationMapper[]>([]);
  const [selectedCode, setSelectedCode] = useState<TranslationMapper | null>(null);
  const [selectedClient, setSelectedClient] = useState<SAPInputOption | null>(null);
  const [codeRows, setCodeRows] = useState<TranslationMapper[]>([]);
  const [translationDataRows, setTranslationDataRows] = useState<TranslationData[]>([]);
  const [isShowingTranslationData, setIsShowingTranslationData] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [codePage, setCodePage] = useState(0);
  const [codeRowCount, setCodeRowCount] = useState(0);
  const codeRowsPerPage = 10;
  const [initialLoading, setInitialLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // State for second page (translation data)
  const [currentView, setCurrentView] = useState<'search' | 'detail'>('search');
  const [selectedCodeForDetail, setSelectedCodeForDetail] = useState<TranslationMapper | null>(null);
  const [translationData, setTranslationData] = useState<TranslationData[]>([]);
  const [translationLoading, setTranslationLoading] = useState(false);
  const [translationError, setTranslationError] = useState<string | null>(null);
  const [translationPage, setTranslationPage] = useState(0);
  const [translationRowCount, setTranslationRowCount] = useState(0);
  const translationDataPerPage = 10;

  // State for translation data filters
  const [sourceValueFilter, setSourceValueFilter] = useState<{ label: string; value: string; code: string } | null>(null);
  const [destinationValueFilter, setDestinationValueFilter] = useState<{ label: string; value: string; code: string } | null>(null);
  const [filteredTranslationData, setFilteredTranslationData] = useState<TranslationData[]>([]);

  // State for advanced filters
  const [sourceFieldFilter, setSourceFieldFilter] = useState<{ label: string; value: string; code: string } | null>(null);
  const [destinationFieldFilter, setDestinationFieldFilter] = useState<{ label: string; value: string; code: string } | null>(null);

  // State for delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [translationToDelete, setTranslationToDelete] = useState<TranslationData | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // State for add translation dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [newTranslation, setNewTranslation] = useState({
    key: '',
    value: '',
    keyDescription: '',
    valueDescription: '',
  });

  // State for edit translation dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [translationToEdit, setTranslationToEdit] = useState<TranslationData | null>(null);
  const [editTranslation, setEditTranslation] = useState({
    key: '',
    value: '',
    keyDescription: '',
    valueDescription: '',
  });

  // State for translation mapper (for dynamic field names)
  const [translationMapper, setTranslationMapper] = useState<TranslationMapper | null>(null);

  // State for Upload CSV dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDelimiter, setUploadDelimiter] = useState<string>(',');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if client code is available
  if (!clientCode) {
    return (
      <Box sx={{ 
        padding: SAP_SPACING.lg, 
        backgroundColor: SAP_COLORS.sapGray1, 
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <SAPDataCard>
          <CardContent sx={{ padding: SAP_SPACING.xl, textAlign: 'center' }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: SAP_COLORS.sapRed,
                fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                marginBottom: SAP_SPACING.md,
              }}
            >
              Client Code Not Available
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: SAP_COLORS.sapGray6,
                fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
              }}
            >
              Please ensure you have selected a client before accessing this page.
            </Typography>
          </CardContent>
        </SAPDataCard>
      </Box>
    );
  }

  // Fetch code options for dropdown
  // Calls: /translationMapper/?firstResult=-1&maxResult=0
  useEffect(() => {
    const fetchCodes = async () => {
      try {
        const codes = await fetchAllTranslationMapperCodes({ clientCode });
        setCodeOptions(codes);
      } catch (err) {
        logger.error('Failed to fetch codes:', err);
        setCodeOptions([]);
      }
    };
    fetchCodes();
  }, [clientCode]);

  // Initial data fetch when component mounts
  useEffect(() => {
    let isMounted = true;
    
    if (clientCode) {
      setInitialLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [clientCode]);

  // Fetch code list data
  // When filters are selected, calls: /clientTranslation/ with clientId or code
  // Otherwise calls: /translationMapper/
  const fetchCodeData = async (pageOverride?: number) => {
    setCodeLoading(true);
    setCodeError(null);
    try {
      const currentPage = pageOverride ?? codePage;
      
      // If client or code filter is selected, use /clientTranslation/ endpoint
      if (selectedClient?.value || selectedCode?.code) {
        const [data, count] = await Promise.all([
          fetchClientTranslationWithSearch({
            clientCode,
            firstResult: currentPage * codeRowsPerPage,
            maxResult: codeRowsPerPage,
            clientId: selectedClient?.value,
            code: selectedCode?.code,
          }),
          fetchClientTranslationCountWithSearch({
            clientCode,
            clientId: selectedClient?.value,
            code: selectedCode?.code,
          })
        ]);
        
        // Store all translation data rows
        setTranslationDataRows(data || []);
        setCodeRows([]);
        setIsShowingTranslationData(true);
        // Count should be unique codes, but API returns total count
        // We'll use the count as is, but display only unique codes
        setCodeRowCount(typeof count === 'number' ? count : 0);
        setHasSearched(true);
        
        // Show message if no results found with filters
        if (data && data.length === 0) {
          setCodeError('No translation data found with the selected filters. Try adjusting your search criteria.');
        } else {
          setCodeError(null);
        }
      } else {
        // No filters selected, use /translationMapper/ endpoint
        const searchParams: any = {
          firstResult: currentPage * codeRowsPerPage,
          maxResult: codeRowsPerPage,
        };

        const [data, count] = await Promise.all([
          fetchTranslationMappers({ 
            clientCode,
            ...searchParams,
          }),
          fetchTranslationMappersCount({ 
            clientCode, 
            ...searchParams,
          })
        ]);
        
        setCodeRows(data || []);
        setTranslationDataRows([]);
        setIsShowingTranslationData(false);
        setCodeRowCount(typeof count === 'number' ? count : 0);
        setHasSearched(true);
        
        if (data && data.length === 0) {
          setCodeError('No translation codes found. Try adjusting your search criteria.');
        } else {
          setCodeError(null);
        }
      }
    } catch (err: any) {
      setCodeError('Failed to load translation data. Please try again.');
      setCodeRows([]);
      setTranslationDataRows([]);
      setCodeRowCount(0);
    } finally {
      setCodeLoading(false);
    }
  };

  // Fetch translation mapper for field names
  const fetchTranslationMapperForCode = async (code: string) => {
    try {
      const mappers = await fetchTranslationMappers({
        clientCode,
        firstResult: -1,
        maxResult: 0,
        code,
      });
      if (mappers && mappers.length > 0) {
        setTranslationMapper(mappers[0]);
      }
    } catch (err) {
      logger.error('Failed to fetch translation mapper:', err);
    }
  };

  // Fetch translation data for detail view
  // Calls: /clientTranslation/?firstResult=0&maxResult=10&searched=true and /clientTranslation/count?&searched=true
  const fetchTranslationDataForDetail = async (code: string, pageOverride?: number) => {
    setTranslationLoading(true);
    setTranslationError(null);
    try {
      const currentPage = pageOverride ?? translationPage;
      // Fetch mapper first to get field names
      await fetchTranslationMapperForCode(code);
      
      const [data, count] = await Promise.all([
        fetchTranslationData({
          clientCode,
          code,
          firstResult: currentPage * translationDataPerPage,
          maxResult: translationDataPerPage,
        }),
        fetchTranslationDataCount({ 
          clientCode,
          code,
        })
      ]);
      setTranslationData(data || []);
      setTranslationRowCount(typeof count === 'number' ? count : 0);
    } catch (err: any) {
      setTranslationError('Failed to load translation data');
      setTranslationData([]);
      setTranslationRowCount(0);
    } finally {
      setTranslationLoading(false);
    }
  };

  // Handle code page change
  const handleCodePageChange = (_: any, newPage: number) => {
    setCodePage(newPage);
    fetchCodeData(newPage);
  };

  // Handle translation page change
  const handleTranslationPageChange = (_: any, newPage: number) => {
    setTranslationPage(newPage);
    if (selectedCodeForDetail) {
      fetchTranslationDataForDetail(selectedCodeForDetail.code, newPage);
    }
  };

  // Handle search
  const handleSearch = () => {
    // Always fetch data when search is clicked, regardless of filters
    setCodePage(0);
    fetchCodeData(0);
  };

  // Handle reset
  const handleReset = () => {
    setSelectedCode(null);
    setSelectedClient(null);
    setSourceFieldFilter(null);
    setDestinationFieldFilter(null);
    setCodePage(0);
    setHasSearched(false);
    setCodeRows([]);
    setTranslationDataRows([]);
    setIsShowingTranslationData(false);
    setCodeRowCount(0);
    setCodeError(null);
    // Don't call fetchCodeData here - we want to hide the table completely
  };

  // Handle filter changes
  const handleCodeChange = (code: TranslationMapper | null) => {
    setSelectedCode(code);
    // Don't auto-fetch data, wait for search button click
  };

  const handleClientChange = (client: SAPInputOption | null) => {
    setSelectedClient(client);
    // Don't auto-fetch data, wait for search button click
  };

  // Handle create
  const handleCreate = () => {
    // TODO: Implement create functionality
    logger.log('Create clicked');
  };

  // Handle create new translation code
  const handleCreateCode = () => {
    // TODO: Implement create translation code modal/form
    logger.log('Create translation code clicked');
  };

  // Handle edit code (navigate to detail view)
  const handleEditCode = (code: TranslationMapper) => {
    setSelectedCodeForDetail(code);
    setCurrentView('detail');
    setTranslationPage(0);
    setTranslationMapper(null); // Reset mapper to show loading state
    fetchTranslationDataForDetail(code.code, 0);
  };

  // Handle back to search view
  const handleBackToSearch = () => {
    setCurrentView('search');
    setSelectedCodeForDetail(null);
    setTranslationData([]);
    setTranslationRowCount(0);
    setTranslationPage(0);
  };

  // Handle add new translation data
  const handleAddTranslation = () => {
    setNewTranslation({
      key: '',
      value: '',
      keyDescription: '',
      valueDescription: '',
    });
    setAddDialogOpen(true);
  };

  // Handle submit add translation
  const handleSubmitAddTranslation = async () => {
    if (!selectedCodeForDetail || !clientCode) return;

    // Validate required fields
    if (!newTranslation.key || !newTranslation.value) {
      const sourceField = translationMapper?.sourceFieldName || 'Key';
      const destField = translationMapper?.destinationFieldName || 'Value';
      toast.error(`Please fill in ${sourceField} and ${destField} fields`);
      return;
    }

    setAddLoading(true);
    try {
      const payload = {
        code: selectedCodeForDetail.code,
        clientId: selectedCodeForDetail.clientId,
        key: newTranslation.key,
        value: newTranslation.value,
        keyDescription: newTranslation.keyDescription || '',
        valueDescription: newTranslation.valueDescription || '',
      };

      const response = await createTranslationData({ clientCode, payload });
      
      if (response.success) {
        toast.success('Translation added successfully');
        setAddDialogOpen(false);
        // Refresh the translation data
        fetchTranslationDataForDetail(selectedCodeForDetail.code, translationPage);
      } else {
        toast.error(response.messages?.join(', ') || 'Failed to add translation');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to add translation');
    } finally {
      setAddLoading(false);
    }
  };

  // Download current translation data as CSV
  const handleDownloadExcel = async () => {
    if (!selectedCodeForDetail || !clientCode) return;
    try {
      const [allData] = await Promise.all([
        fetchTranslationData({
          clientCode,
          code: selectedCodeForDetail.code,
          clientId: selectedCodeForDetail.clientId,
          firstResult: 0,
          maxResult: 100000,
          searched: true,
        }),
      ]);
      if (!allData || allData.length === 0) {
        toast.info('No translation data to export');
        return;
      }
      const sourceName = translationMapper?.sourceFieldName || 'Key';
      const destName = translationMapper?.destinationFieldName || 'Value';
      const sourceDesc = translationMapper?.sourceFieldDescription || 'Key Description';
      const destDesc = translationMapper?.destinationFieldDescription || 'Value Description';
      const rows = allData.map((row) => ({
        [sourceName]: row.key,
        [destName]: row.value,
        [sourceDesc]: row.keyDescription || '',
        [destDesc]: row.valueDescription || '',
      }));
      // Build CSV manually (no 5th Reciprocal column)
      const headers = [sourceName, destName, sourceDesc, destDesc];
      const csvLines = [
        headers.join(','), // header row
        ...rows.map((row) =>
          headers
            .map((h) => {
              const value = row[h] ?? '';
              const str = String(value);
              // Escape quotes and wrap if needed
              if (str.includes('"') || str.includes(',') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
              }
              return str;
            })
            .join(',')
        ),
      ];
      const csvContent = csvLines.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `client-translation-${selectedCodeForDetail.code}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('CSV file downloaded');
    } catch (err: any) {
      toast.error(err.message || 'Failed to download CSV');
    }
  };

  // Upload CSV: open dialog (delimiter selection + file)
  const handleUploadCSV = () => {
    setUploadDelimiter(',');
    setUploadFile(null);
    setUploadError(null);
    setUploadDialogOpen(true);
  };

  // Parse CSV and bulk create translations
  const handleSubmitUploadCSV = async () => {
    if (!selectedCodeForDetail || !clientCode || !uploadFile) {
      toast.error('Please select a CSV file');
      return;
    }
    setUploadLoading(true);
    setUploadError(null);
    try {
      const text = await uploadFile.text();
      const delimiter = uploadDelimiter === 'tab' ? '\t' : uploadDelimiter;
      const lines = text.split(/\r?\n/).filter((line) => line.trim());
      if (lines.length === 0) {
        setUploadError('File is empty');
        setUploadLoading(false);
        return;
      }
      const rows = lines.map((line) => parseCSVLine(line, delimiter));
      const first = rows[0] || [];
      const firstCell = String(first[0] ?? '').toLowerCase().replace(/\s/g, '');
      const isHeader = firstCell === 'key' || firstCell === 'value' || firstCell === (translationMapper?.sourceFieldName || '').toLowerCase().replace(/\s/g, '');
      const dataStart = isHeader && rows.length > 1 ? 1 : 0;
      const keyCol = 0;
      const valueCol = 1;
      let created = 0;
      let failed = 0;
      for (let i = dataStart; i < rows.length; i++) {
        const key = String(rows[i][keyCol] ?? '').trim();
        const value = String(rows[i][valueCol] ?? '').trim();
        if (!key && !value) continue;
        if (!key || !value) {
          failed++;
          continue;
        }
        try {
          const payload = {
            code: selectedCodeForDetail.code,
            clientId: selectedCodeForDetail.clientId,
            key,
            value,
            keyDescription: String(rows[i][2] ?? '').trim(),
            valueDescription: String(rows[i][3] ?? '').trim(),
          };
          const res = await createTranslationData({ clientCode, payload });
          if (res.success) created++;
          else failed++;
        } catch {
          failed++;
        }
      }
      setUploadDialogOpen(false);
      setUploadFile(null);
      toast.success(`Upload complete: ${created} added${failed ? `, ${failed} failed or skipped` : ''}`);
      fetchTranslationDataForDetail(selectedCodeForDetail.code, translationPage);
    } catch (err: any) {
      setUploadError(err.message || 'Failed to parse or upload CSV');
      toast.error('Upload failed');
    } finally {
      setUploadLoading(false);
    }
  };

  // Parse a single CSV line respecting quoted fields
  function parseCSVLine(line: string, delimiter: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (!inQuotes && ch === delimiter) {
        result.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  }

  // Handle edit translation data
  const handleEditTranslation = (translation: TranslationData) => {
    setTranslationToEdit(translation);
    setEditTranslation({
      key: translation.key,
      value: translation.value,
      keyDescription: translation.keyDescription || '',
      valueDescription: translation.valueDescription || '',
    });
    setEditDialogOpen(true);
  };

  // Handle submit edit translation
  const handleSubmitEditTranslation = async () => {
    if (!translationToEdit || !clientCode || !selectedCodeForDetail) return;

    // Validate required fields
    if (!editTranslation.key || !editTranslation.value) {
      const sourceField = translationMapper?.sourceFieldName || 'Key';
      const destField = translationMapper?.destinationFieldName || 'Value';
      toast.error(`Please fill in ${sourceField} and ${destField} fields`);
      return;
    }

    setEditLoading(true);
    try {
      // Build payload with all required fields from the original translation
      const payload: TranslationData = {
        ...translationToEdit,
        key: editTranslation.key,
        value: editTranslation.value,
        keyDescription: editTranslation.keyDescription || '',
        valueDescription: editTranslation.valueDescription || '',
        lastUpdateDate: Date.now(), // Update timestamp
      };

      await updateTranslationData({
        clientCode,
        payload,
      });

      toast.success('Translation updated successfully');
      setEditDialogOpen(false);
      setTranslationToEdit(null);
      // Refresh the translation data
      fetchTranslationDataForDetail(selectedCodeForDetail.code, translationPage);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update translation');
    } finally {
      setEditLoading(false);
    }
  };

  // Handle delete translation data
  const handleDeleteTranslation = (translation: TranslationData) => {
    setTranslationToDelete(translation);
    setDeleteDialogOpen(true);
  };

  // Confirm delete translation
  const handleConfirmDelete = async () => {
    if (!translationToDelete || !clientCode) return;

    setDeleteLoading(true);
    try {
      const response = await deleteTranslationData({
        clientCode,
        translationData: translationToDelete,
      });

      if (response.success) {
        toast.success('Translation deleted successfully');
        setDeleteDialogOpen(false);
        setTranslationToDelete(null);
        // Refresh the translation data
        if (selectedCodeForDetail) {
          fetchTranslationDataForDetail(selectedCodeForDetail.code, translationPage);
        }
      } else {
        toast.error(response.messages?.join(', ') || 'Failed to delete translation');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete translation');
    } finally {
      setDeleteLoading(false);
    }
  };


  if (currentView === 'detail') {
    return (
      <Box sx={{ 
        padding: SAP_SPACING.lg, 
        backgroundColor: SAP_COLORS.sapGray1, 
        minHeight: '100vh' 
      }}>
        {/* SAP Page Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: SAP_SPACING.xl 
        }}>
          <IconButton 
            onClick={handleBackToSearch}
            sx={{ 
              marginRight: SAP_SPACING.md,
              backgroundColor: SAP_COLORS.sapGray2,
              color: SAP_COLORS.sapGray7,
              '&:hover': {
                backgroundColor: SAP_COLORS.sapGray3,
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="h4" 
            sx={{ 
              color: SAP_COLORS.sapGray7,
              fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
              display: 'flex',
              alignItems: 'center',
              gap: SAP_SPACING.sm,
            }}
          >
            <LanguageIcon sx={{ color: SAP_COLORS.sapBlue, fontSize: '2rem' }} />
            Client Translation
          </Typography>
        </Box>


        {/* SAP Translation Data Filters */}
        <SAPDataCard>
          <CardContent sx={{ padding: SAP_SPACING.lg }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: SAP_COLORS.sapGray7,
                fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                marginBottom: SAP_SPACING.md,
              }}
            >
              Filter Translation Data
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              gap: SAP_SPACING.md, 
              alignItems: { xs: 'stretch', md: 'center' } 
            }}>
              {/* Source Value Filter */}
              <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 250 } }}>
                <Autocomplete
                  options={translationData.map(item => ({
                    label: item.key,
                    value: item.key,
                    code: item.key
                  }))}
                  value={sourceValueFilter}
                  onChange={(event, newValue) => setSourceValueFilter(newValue)}
                  getOptionLabel={(option) => option.label || ''}
                  renderInput={(params) => (
                    <TextField {...params} label="Source Value" />
                  )}
                />
              </Box>

              {/* Destination Value Filter */}
              <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 250 } }}>
                <Autocomplete
                  options={translationData.map(item => ({
                    label: item.value,
                    value: item.value,
                    code: item.value
                  }))}
                  value={destinationValueFilter}
                  onChange={(event, newValue) => setDestinationValueFilter(newValue)}
                  getOptionLabel={(option) => option.label || ''}
                  renderInput={(params) => (
                    <TextField {...params} label="Destination Value" />
                  )}
                />
              </Box>

              {/* Filter Actions */}
              <Box sx={{ 
                display: 'flex', 
                gap: SAP_SPACING.sm, 
                minWidth: { xs: '100%', md: 140 },
                justifyContent: { xs: 'center', md: 'flex-start' }
              }}>
                <SAPIconButton
                  icon="search"
                  onClick={() => {
                    // Apply filters to translation data
                    let filtered = translationData;
                    if (sourceValueFilter) {
                      filtered = filtered.filter(item => item.key === sourceValueFilter.value);
                    }
                    if (destinationValueFilter) {
                      filtered = filtered.filter(item => item.value === destinationValueFilter.value);
                    }
                    setFilteredTranslationData(filtered);
                  }}
                  variant="primary"
                  aria-label="Apply filters"
                />
                <SAPIconButton
                  icon="reset"
                  onClick={() => {
                    setSourceValueFilter(null);
                    setDestinationValueFilter(null);
                    setFilteredTranslationData([]);
                  }}
                  variant="secondary"
                  aria-label="Clear filters"
                />
              </Box>
            </Box>
          </CardContent>
        </SAPDataCard>

        {/* SAP Translation Data Table */}
        <SAPDataCard>
          <CardContent sx={{ padding: SAP_SPACING.lg }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: SAP_SPACING.lg 
            }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: SAP_COLORS.sapGray7,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                }}
              >
                Client Translation
              </Typography>
              {(sourceValueFilter || destinationValueFilter) && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: SAP_COLORS.sapBlue,
                    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                    fontSize: '0.875rem',
                  }}
                >
                  Filters applied: 
                  {sourceValueFilter && ` Source: ${sourceValueFilter.label}`}
                  {destinationValueFilter && ` Destination: ${destinationValueFilter.label}`}
                  {filteredTranslationData.length > 0 && ` (${filteredTranslationData.length} results)`}
                </Typography>
              )}
            </Box>
              <Box sx={{ display: 'flex', gap: SAP_SPACING.sm }}>
                <SAPIconButton
                  icon="download"
                  onClick={handleDownloadExcel}
                  variant="primary"
                  tooltip="Download Excel"
                  aria-label="Download Excel"
                />
                <Tooltip title="Upload CSV">
                  <span>
                    <IconButton
                      onClick={handleUploadCSV}
                      size="medium"
                      sx={{
                        backgroundColor: SAP_COLORS.sapBlue,
                        color: SAP_COLORS.sapWhite,
                        '&:hover': { backgroundColor: SAP_COLORS.sapDarkBlue },
                      }}
                      aria-label="Upload CSV"
                    >
                      <UploadIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <SAPIconButton
                  icon="add"
                  onClick={handleAddTranslation}
                  variant="primary"
                  aria-label="Add Translation"
                />
              </Box>
            </Box>

            {/* SAP Error Alert */}
            {translationError && (
              <Alert 
                severity="error" 
                sx={{ 
                  marginBottom: SAP_SPACING.lg,
                  backgroundColor: SAP_COLORS.sapLightRed,
                  color: SAP_COLORS.sapRed,
                }}
                action={
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={() => {
                      if (selectedCodeForDetail) {
                        fetchTranslationDataForDetail(selectedCodeForDetail.code, translationPage);
                      }
                    }}
                    sx={{ 
                      color: SAP_COLORS.sapRed,
                      fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                    }}
                  >
                    Retry
                  </Button>
                }
              >
                {translationError}
              </Alert>
            )}

            <TableContainer 
              component={Card} 
              sx={{ 
                boxShadow: 'none', 
                backgroundColor: SAP_COLORS.sapWhite 
              }}
            >
              {translationLoading ? (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  padding: SAP_SPACING.xl 
                }}>
                  <CircularProgress 
                    size={40} 
                    sx={{ color: SAP_COLORS.sapBlue }} 
                  />
                </Box>
              ) : (
                <Table sx={{ minWidth: 900 }} stickyHeader>
                  <SAPTableHeader>
                    <TableRow>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray7,
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                        borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                      }}>
                        {translationMapper?.sourceFieldName || 'Source Field'}
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray7,
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                        borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                      }}>
                        {translationMapper?.destinationFieldName || 'Destination Field'}
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6,
                        borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                      }}>
                        {translationMapper?.sourceFieldDescription || 'Source Description'}
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6,
                        borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                      }}>
                        {translationMapper?.destinationFieldDescription || 'Destination Description'}
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6,
                        borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                      }}>
                        Reciprocal
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${SAP_COLORS.sapGray2}` }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </SAPTableHeader>
                  <TableBody sx={{ backgroundColor: SAP_COLORS.sapWhite }}>
                    {(filteredTranslationData.length > 0 ? filteredTranslationData : translationData).length === 0 ? (
                      <TableRow>
                        <TableCell 
                          colSpan={6} 
                          align="center" 
                          sx={{ 
                            color: SAP_COLORS.sapGray4,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                            padding: SAP_SPACING.xl,
                          }}
                        >
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                color: SAP_COLORS.sapGray5,
                                fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                                marginBottom: SAP_SPACING.sm,
                              }}
                            >
                              No translation data found for this code
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: SAP_COLORS.sapGray4,
                                fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
                              }}
                            >
                              Use the "Add" button above to create the first translation entry
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      (filteredTranslationData.length > 0 ? filteredTranslationData : translationData).map((row, idx) => (
                        <TableRow
                          key={row.id}
                          sx={{
                            backgroundColor: idx % 2 === 0 ? SAP_COLORS.sapWhite : SAP_COLORS.sapGray1,
                            '&:hover': { 
                              backgroundColor: SAP_COLORS.hover,
                              cursor: 'pointer',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray7,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                          }}>
                            {row.key}
                          </TableCell>
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray7,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                          }}>
                            {row.value}
                          </TableCell>
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray6,
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                          }}>
                            {row.keyDescription}
                          </TableCell>
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray6,
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                          }}>
                            {row.valueDescription}
                          </TableCell>
                          <TableCell sx={{ 
                            color: row.reciprocal ? SAP_COLORS.sapGreen : SAP_COLORS.sapRed,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                          }}>
                            {row.reciprocal ? 'true' : 'false'}
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${SAP_COLORS.sapGray2}` }}>
                            <Box sx={{ display: 'flex', gap: SAP_SPACING.xs }}>
                              <SAPIconButton
                                icon="edit"
                                onClick={() => handleEditTranslation(row)}
                                variant="secondary"
                                aria-label="Edit translation"
                              />
                              <SAPIconButton
                                icon="delete"
                                onClick={() => handleDeleteTranslation(row)}
                                variant="secondary"
                                aria-label="Delete translation"
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </TableContainer>

            {/* SAP Pagination */}
            {!translationLoading && translationData.length > 0 && (
              <>
                {/* Pagination */}
                {translationData.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {/* Pagination Info */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {(translationPage * translationDataPerPage) + 1}-{Math.min((translationPage * translationDataPerPage) + translationData.length, translationRowCount)} of {translationRowCount} translations
                      </Typography>
                    </Box>
                    
                    {/* Pagination Controls */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Rows per page</InputLabel>
                        <Select
                          value={translationDataPerPage}
                          onChange={(event: SelectChangeEvent<number>) => {
                            // Note: translationDataPerPage is constant, so this is just for UI consistency
                          }}
                          label="Rows per page"
                        >
                          <MenuItem value={5}>5</MenuItem>
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={25}>25</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <Pagination
                        count={Math.ceil(translationRowCount / translationDataPerPage)}
                        page={translationPage + 1}
                        onChange={(event: React.ChangeEvent<unknown>, newPage: number) => {
                          handleTranslationPageChange(event, newPage - 1);
                        }}
                        color="primary"
                        showFirstButton
                        showLastButton
                      />
                    </Box>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </SAPDataCard>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ color: SAP_COLORS.sapGray7, fontWeight: SAP_TYPOGRAPHY.fontWeight.bold }}>
            Delete Translation
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: SAP_COLORS.sapGray6, mb: 2 }}>
              Are you sure you want to delete this translation?
            </Typography>
            {translationToDelete && (
              <Box sx={{ 
                p: 2, 
                bgcolor: SAP_COLORS.sapGray1, 
                borderRadius: '8px',
                border: `1px solid ${SAP_COLORS.sapGray2}` 
              }}>
                <Typography variant="body2" sx={{ color: SAP_COLORS.sapGray7, fontWeight: SAP_TYPOGRAPHY.fontWeight.medium }}>
                  <strong>Key:</strong> {translationToDelete.key}
                </Typography>
                <Typography variant="body2" sx={{ color: SAP_COLORS.sapGray7, mt: 1 }}>
                  <strong>Value:</strong> {translationToDelete.value}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteLoading}
              sx={{ color: SAP_COLORS.sapGray6 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={deleteLoading}
              variant="contained"
              sx={{
                backgroundColor: SAP_COLORS.sapRed,
                color: SAP_COLORS.sapWhite,
                '&:hover': {
                  backgroundColor: '#B01515',
                },
              }}
            >
              {deleteLoading ? <CircularProgress size={20} sx={{ color: SAP_COLORS.sapWhite }} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Translation Dialog */}
        <Dialog
          open={addDialogOpen}
          onClose={() => !addLoading && setAddDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ color: SAP_COLORS.sapGray7, fontWeight: SAP_TYPOGRAPHY.fontWeight.bold }}>
            Add New Translation
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label={`${translationMapper?.sourceFieldName || 'Key'} *`}
                value={newTranslation.key}
                onChange={(e) => setNewTranslation({ ...newTranslation, key: e.target.value })}
                fullWidth
                required
                disabled={addLoading}
              />
              <TextField
                label={`${translationMapper?.destinationFieldName || 'Value'} *`}
                value={newTranslation.value}
                onChange={(e) => setNewTranslation({ ...newTranslation, value: e.target.value })}
                fullWidth
                required
                disabled={addLoading}
              />
              <TextField
                label={translationMapper?.sourceFieldDescription || 'Key Description'}
                value={newTranslation.keyDescription}
                onChange={(e) => setNewTranslation({ ...newTranslation, keyDescription: e.target.value })}
                fullWidth
                disabled={addLoading}
              />
              <TextField
                label={translationMapper?.destinationFieldDescription || 'Value Description'}
                value={newTranslation.valueDescription}
                onChange={(e) => setNewTranslation({ ...newTranslation, valueDescription: e.target.value })}
                fullWidth
                disabled={addLoading}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setAddDialogOpen(false)}
              disabled={addLoading}
              sx={{ color: SAP_COLORS.sapGray6 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitAddTranslation}
              disabled={addLoading}
              variant="contained"
              sx={{
                backgroundColor: SAP_COLORS.sapBlue,
                color: SAP_COLORS.sapWhite,
                '&:hover': {
                  backgroundColor: SAP_COLORS.sapBlueDark,
                },
              }}
            >
              {addLoading ? <CircularProgress size={20} sx={{ color: SAP_COLORS.sapWhite }} /> : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Translation Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => !editLoading && setEditDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ color: SAP_COLORS.sapGray7, fontWeight: SAP_TYPOGRAPHY.fontWeight.bold }}>
            Edit Translation
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label={`${translationMapper?.sourceFieldName || 'Key'} *`}
                value={editTranslation.key}
                onChange={(e) => setEditTranslation({ ...editTranslation, key: e.target.value })}
                fullWidth
                required
                disabled={editLoading}
              />
              <TextField
                label={`${translationMapper?.destinationFieldName || 'Value'} *`}
                value={editTranslation.value}
                onChange={(e) => setEditTranslation({ ...editTranslation, value: e.target.value })}
                fullWidth
                required
                disabled={editLoading}
              />
              <TextField
                label={translationMapper?.sourceFieldDescription || 'Key Description'}
                value={editTranslation.keyDescription}
                onChange={(e) => setEditTranslation({ ...editTranslation, keyDescription: e.target.value })}
                fullWidth
                disabled={editLoading}
              />
              <TextField
                label={translationMapper?.destinationFieldDescription || 'Value Description'}
                value={editTranslation.valueDescription}
                onChange={(e) => setEditTranslation({ ...editTranslation, valueDescription: e.target.value })}
                fullWidth
                disabled={editLoading}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setEditDialogOpen(false)}
              disabled={editLoading}
              sx={{ color: SAP_COLORS.sapGray6 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitEditTranslation}
              disabled={editLoading}
              variant="contained"
              sx={{
                backgroundColor: SAP_COLORS.sapBlue,
                color: SAP_COLORS.sapWhite,
                '&:hover': {
                  backgroundColor: SAP_COLORS.sapBlueDark,
                },
              }}
            >
              {editLoading ? <CircularProgress size={20} sx={{ color: SAP_COLORS.sapWhite }} /> : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Upload CSV Dialog */}
        <Dialog
          open={uploadDialogOpen}
          onClose={() => !uploadLoading && (setUploadDialogOpen(false), setUploadFile(null), setUploadError(null))}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ color: SAP_COLORS.sapGray7, fontWeight: SAP_TYPOGRAPHY.fontWeight.bold }}>
            Upload CSV
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <input
                type="file"
                accept=".csv,.txt"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  setUploadFile(file || null);
                  setUploadError(null);
                }}
              />
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ alignSelf: 'flex-start' }}
              >
                {uploadFile ? uploadFile.name : 'Choose CSV file'}
              </Button>
              <FormControl fullWidth size="small">
                <InputLabel>Delimiter</InputLabel>
                <Select
                  value={uploadDelimiter}
                  label="Delimiter"
                  onChange={(e) => setUploadDelimiter(e.target.value)}
                  disabled={uploadLoading}
                >
                  <MenuItem value=",">Comma (,)</MenuItem>
                  <MenuItem value=";">Semicolon (;)</MenuItem>
                  <MenuItem value="tab">Tab</MenuItem>
                  <MenuItem value="|">Pipe (|)</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" sx={{ color: SAP_COLORS.sapGray6 }}>
                CSV should have columns: {translationMapper?.sourceFieldName || 'Key'}, {translationMapper?.destinationFieldName || 'Value'} (optional: descriptions). First row may be a header.
              </Typography>
              {uploadError && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => setUploadError(null)}>
                  {uploadError}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => !uploadLoading && (setUploadDialogOpen(false), setUploadFile(null), setUploadError(null))}
              disabled={uploadLoading}
              sx={{ color: SAP_COLORS.sapGray6 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitUploadCSV}
              disabled={uploadLoading || !uploadFile}
              variant="contained"
              sx={{
                backgroundColor: SAP_COLORS.sapBlue,
                color: SAP_COLORS.sapWhite,
                '&:hover': { backgroundColor: SAP_COLORS.sapBlueDark },
              }}
            >
              {uploadLoading ? <CircularProgress size={20} sx={{ color: SAP_COLORS.sapWhite }} /> : 'Upload'}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    );
  }

  return (
    <Box sx={{ 
      padding: SAP_SPACING.lg, 
      backgroundColor: SAP_COLORS.sapGray1, 
      minHeight: '100vh' 
    }}>
      {/* Page Header */}
      <PageHeader 
        title="Client Translation Search"
        description="Search and manage client translation codes"
      />

      <SAPFilterCard>
        <CardContent sx={{ padding: SAP_SPACING.lg }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            gap: SAP_SPACING.md, 
            alignItems: { xs: 'stretch', md: 'center' } 
          }}>
            {/* Code Filter */}
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 250 } }}>
              <Autocomplete
                options={codeOptions}
                value={selectedCode}
                onChange={(event, newValue) => handleCodeChange(newValue)}
                getOptionLabel={(option) => option.code || ''}
                renderInput={(params) => (
                  <TextField {...params} label="Translation Code" />
                )}
              />
            </Box>

            {/* Client Filter - admin only */}
            {isAdmin && (
              <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 250 } }}>
                <Autocomplete
                  options={clientOptions}
                  value={selectedClient}
                  onChange={(event, newValue) => handleClientChange(newValue)}
                  getOptionLabel={(option) => option.label || ''}
                  renderInput={(params) => (
                    <TextField {...params} label="Client" />
                  )}
                />
              </Box>
            )}

            {/* Search Actions */}
            <Box sx={{ 
              display: 'flex', 
              gap: SAP_SPACING.sm, 
              minWidth: { xs: '100%', md: 140 },
              justifyContent: { xs: 'center', md: 'flex-start' }
            }}>
              <SAPIconButton
                icon="search"
                onClick={handleSearch}
                variant="primary"
                aria-label="Search"
              />
              <SAPIconButton
                icon="reset"
                onClick={handleReset}
                variant="secondary"
                aria-label="Clear Filters"
              />
            </Box>
          </Box>
        </CardContent>
      </SAPFilterCard>


      {/* SAP Error Alert */}
      {codeError && (
        <Alert 
          severity="error" 
          sx={{ 
            marginBottom: SAP_SPACING.lg, 
            borderRadius: '8px',
            backgroundColor: SAP_COLORS.sapLightRed,
            color: SAP_COLORS.sapRed,
            border: `1px solid ${SAP_COLORS.sapRed}`,
          }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => fetchCodeData(codePage)}
              sx={{ 
                color: SAP_COLORS.sapRed,
                fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
              }}
            >
              Retry
            </Button>
          }
        >
          {codeError}
        </Alert>
      )}

      {/* SAP Data Display */}
      <SAPDataCard>
        <CardContent sx={{ p: 0 }}>
          {!hasSearched ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <Typography variant="body1" sx={{ color: SAP_COLORS.sapGray5 }}>
                Please use the search filters above to view translation codes.
              </Typography>
            </Box>
          ) : codeLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <CircularProgress sx={{ color: SAP_COLORS.sapBlue }} size={48} />
            </Box>
          ) : codeError ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <Alert severity="error" sx={{ 
                borderRadius: '8px',
                backgroundColor: SAP_COLORS.sapRed + '10',
                color: SAP_COLORS.sapRed,
                border: `1px solid ${SAP_COLORS.sapRed}30`,
              }}>
                {codeError}
              </Alert>
            </Box>
          ) : (
            <>
              {/* Search Summary */}
              

              {codeLoading ? (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  padding: SAP_SPACING.md,
                  backgroundColor: SAP_COLORS.sapGray1,
                  borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                }}>
                  <CircularProgress 
                    size={24} 
                    sx={{ color: SAP_COLORS.sapBlue, marginRight: SAP_SPACING.sm }} 
                  />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: SAP_COLORS.sapGray6,
                      fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                    }}
                  >
                    Updating results...
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* SAP Table */}
                  <TableContainer 
                    component={Card} 
                    sx={{ 
                      boxShadow: 'none', 
                      backgroundColor: SAP_COLORS.sapWhite 
                    }}
                  >
                    <Table sx={{ minWidth: 900 }} stickyHeader aria-label="translation codes table">
                      <SAPTableHeader>
                        <TableRow>
                          <TableCell sx={{ 
                            backgroundColor: SAP_COLORS.sapLightBlue,
                            color: SAP_COLORS.sapGray6,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                          }}>
                            S.No.
                          </TableCell>
                          <TableCell sx={{ 
                            backgroundColor: SAP_COLORS.sapLightBlue,
                            color: SAP_COLORS.sapGray7,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                          }}>
                            Code
                          </TableCell>
                          <TableCell sx={{ 
                            backgroundColor: SAP_COLORS.sapLightBlue,
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                            textAlign: 'center',
                          }}>
                            Action
                          </TableCell>
                        </TableRow>
                      </SAPTableHeader>
                      
                      {/* Table Body */}
                      <TableBody sx={{ backgroundColor: SAP_COLORS.sapWhite }}>
                        {isShowingTranslationData ? (
                          // Show TranslationData when filters are selected - only Code, S.No., and Edit icon
                          translationDataRows.length === 0 ? (
                            <TableRow>
                              <TableCell 
                                colSpan={3} 
                                align="center" 
                                sx={{ 
                                  color: SAP_COLORS.sapGray5,
                                  py: SAP_SPACING.xl,
                                  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                                }}
                              >
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography 
                                    variant="body1" 
                                    sx={{ 
                                      color: SAP_COLORS.sapGray5,
                                      fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                                      marginBottom: SAP_SPACING.sm,
                                    }}
                                  >
                                    No translation data found with the selected filters
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: SAP_COLORS.sapGray4,
                                      fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
                                    }}
                                  >
                                    Try adjusting your search criteria or use the "Clear Filters" button
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ) : (
                            (() => {
                              // Get unique codes from the data to show each code only once
                              const uniqueCodes = Array.from(new Set(translationDataRows.map(r => r.code)));
                              
                              return uniqueCodes.map((code, codeIndex) => {
                                // Get the first row with this code for reference
                                const firstRow = translationDataRows.find(r => r.code === code);
                                if (!firstRow) return null;
                                
                                return (
                                  <TableRow
                                    key={code}
                                    sx={{
                                      bgcolor: codeIndex % 2 === 0 ? SAP_COLORS.sapWhite : SAP_COLORS.sapGray1,
                                      '&:hover': { 
                                        bgcolor: SAP_COLORS.hover,
                                        cursor: 'pointer',
                                      },
                                      transition: 'all 0.2s ease',
                                    }}
                                  >
                                    <TableCell sx={{ 
                                      color: SAP_COLORS.sapGray6,
                                      fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                                      borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                                    }}>
                                      {codePage * codeRowsPerPage + codeIndex + 1}
                                    </TableCell>
                                    <TableCell sx={{ 
                                      color: SAP_COLORS.sapGray7,
                                      fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                                      borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                                    }}>
                                      {code}
                                    </TableCell>
                                    <TableCell sx={{ 
                                      borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                                      textAlign: 'center',
                                      padding: SAP_SPACING.md,
                                    }}>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // Find the mapper for this code to navigate to detail view
                                          const mapper = codeOptions.find(opt => opt.code === code);
                                          if (mapper) {
                                            handleEditCode(mapper);
                                          }
                                        }}
                                        sx={{ color: 'primary.main' }}
                                        aria-label="View translation details"
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                );
                              });
                            })()
                          )
                        ) : (
                          // Show TranslationMapper when no filters are selected
                          codeRows.length === 0 ? (
                            <TableRow>
                              <TableCell 
                                colSpan={3} 
                                align="center" 
                                sx={{ 
                                  color: SAP_COLORS.sapGray5,
                                  py: SAP_SPACING.xl,
                                  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                                }}
                              >
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography 
                                    variant="body1" 
                                    sx={{ 
                                      color: SAP_COLORS.sapGray5,
                                      fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                                      marginBottom: SAP_SPACING.sm,
                                    }}
                                  >
                                    {selectedCode || selectedClient 
                                      ? 'No translation codes found with the selected filters'
                                      : 'No translation codes available'
                                    }
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: SAP_COLORS.sapGray4,
                                      fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
                                    }}
                                  >
                                    {selectedCode || selectedClient 
                                      ? 'Try adjusting your search criteria or use the "Clear Filters" button'
                                      : 'Use the "Create" button above to add the first translation code'
                                    }
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ) : (
                            codeRows.map((row, idx) => (
                              <TableRow
                                key={row.id}
                                sx={{
                                  bgcolor: idx % 2 === 0 ? SAP_COLORS.sapWhite : SAP_COLORS.sapGray1,
                                  '&:hover': { 
                                    bgcolor: SAP_COLORS.hover,
                                    cursor: 'pointer',
                                  },
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                <TableCell sx={{ 
                                  color: SAP_COLORS.sapGray6,
                                  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                                  borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                                }}>
                                  {codePage * codeRowsPerPage + idx + 1}
                                </TableCell>
                                <TableCell sx={{ 
                                  color: SAP_COLORS.sapGray7,
                                  fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                                  borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                                }}>
                                  {row.code}
                                </TableCell>
                                <TableCell sx={{ 
                                  borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                                  textAlign: 'center',
                                }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditCode(row)}
                                    sx={{ color: 'primary.main' }}
                                    aria-label="View translation details"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* SAP Pagination */}
                  {/* Pagination */}
                  {(codeRows.length > 0 || translationDataRows.length > 0) && (
                    <Box sx={{ mt: 2 }}>
                      {/* Pagination Info */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Showing {(codePage * codeRowsPerPage) + 1}-{Math.min((codePage * codeRowsPerPage) + (codeRows.length + translationDataRows.length), codeRowCount)} of {codeRowCount} translation codes
                        </Typography>
                      </Box>
                      
                      {/* Pagination Controls */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Rows per page</InputLabel>
                          <Select
                            value={codeRowsPerPage}
                            onChange={(event: SelectChangeEvent<number>) => {
                              // Note: codeRowsPerPage is constant, so this is just for UI consistency
                            }}
                            label="Rows per page"
                          >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                          </Select>
                        </FormControl>
                        
                        <Pagination
                          count={Math.ceil(codeRowCount / codeRowsPerPage)}
                          page={codePage + 1}
                          onChange={(event: React.ChangeEvent<unknown>, newPage: number) => {
                            handleCodePageChange(event, newPage - 1);
                          }}
                          color="primary"
                          showFirstButton
                          showLastButton
                        />
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </CardContent>
      </SAPDataCard>
    </Box>
  );
}